/**
 * User Action Operations
 *
 * Handles all user action operations including preferences, dismissals, and UI state.
 * Replaces localStorage-based storage with IndexedDB-backed SQLite.
 */

import { query, queryOne, execute, saveDB } from '../../db';

// ============================================================================
// Types
// ============================================================================

type ActionType = 'preference' | 'dismissal' | 'ui_state';

type UserAction = {
  id: string;
  action_type: ActionType;
  action_key: string;
  action_value: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
};

// ============================================================================
// Generic Operations
// ============================================================================

/**
 * Set a user action (upsert)
 */
export async function setUserAction(
  actionType: ActionType,
  actionKey: string,
  actionValue: string | null = null,
  expiresAt: string | null = null
): Promise<{ success: boolean; error?: string }> {
  try {
    const id = `${actionType}_${actionKey}`;
    const now = new Date().toISOString();

    execute(
      `INSERT INTO user_actions (id, action_type, action_key, action_value, expires_at, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(action_type, action_key) DO UPDATE SET
         action_value = excluded.action_value,
         expires_at = excluded.expires_at,
         updated_at = excluded.updated_at`,
      [id, actionType, actionKey, actionValue, expiresAt, now, now]
    );

    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[userActionOperations] Error setting user action:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to set user action'
    };
  }
}

/**
 * Get a user action by type and key
 */
export function getUserAction(actionType: ActionType, actionKey: string): UserAction | null {
  try {
    const row = queryOne<UserAction>(
      'SELECT * FROM user_actions WHERE action_type = ? AND action_key = ?',
      [actionType, actionKey]
    );

    // Check if expired
    if (row && row.expires_at) {
      const expiresAt = new Date(row.expires_at);
      if (expiresAt <= new Date()) {
        // Expired, delete it asynchronously
        deleteUserAction(actionType, actionKey);
        return null;
      }
    }

    return row;
  } catch (error) {
    console.error('[userActionOperations] Error getting user action:', error);
    return null;
  }
}

/**
 * Delete a user action
 */
export async function deleteUserAction(
  actionType: ActionType,
  actionKey: string
): Promise<{ success: boolean; error?: string }> {
  try {
    execute('DELETE FROM user_actions WHERE action_type = ? AND action_key = ?', [
      actionType,
      actionKey
    ]);
    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[userActionOperations] Error deleting user action:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete user action'
    };
  }
}

/**
 * Get all user actions of a specific type
 */
export function getUserActions(actionType: ActionType): UserAction[] {
  try {
    const rows = query<UserAction>('SELECT * FROM user_actions WHERE action_type = ?', [
      actionType
    ]);

    // Filter out expired actions
    const now = new Date();
    return rows.filter((row) => {
      if (row.expires_at) {
        const expiresAt = new Date(row.expires_at);
        return expiresAt > now;
      }
      return true;
    });
  } catch (error) {
    console.error('[userActionOperations] Error getting user actions:', error);
    return [];
  }
}

/**
 * Clear expired user actions
 */
export async function clearExpiredActions(): Promise<{ success: boolean; deleted: number }> {
  try {
    const now = new Date().toISOString();

    // Count expired actions before deleting
    const expiredCount = query<{ count: number }>(
      'SELECT COUNT(*) as count FROM user_actions WHERE expires_at IS NOT NULL AND expires_at <= ?',
      [now]
    );
    const count = expiredCount.length > 0 ? expiredCount[0].count : 0;

    // Delete expired actions
    execute(
      'DELETE FROM user_actions WHERE expires_at IS NOT NULL AND expires_at <= ?',
      [now]
    );
    await saveDB();
    return { success: true, deleted: count };
  } catch (error) {
    console.error('[userActionOperations] Error clearing expired actions:', error);
    return { success: false, deleted: 0 };
  }
}

// ============================================================================
// Preference Operations
// ============================================================================

/**
 * Set a user preference
 */
export async function setUserPreference(
  key: string,
  value: string
): Promise<{ success: boolean; error?: string }> {
  return setUserAction('preference', key, value, null);
}

/**
 * Get a user preference value
 */
export function getUserPreference(key: string): string | null {
  const action = getUserAction('preference', key);
  return action?.action_value || null;
}

/**
 * Get all user preferences as a key-value object
 */
export function getAllUserPreferences(): Record<string, string> {
  try {
    const actions = getUserActions('preference');
    const preferences: Record<string, string> = {};

    for (const action of actions) {
      if (action.action_value !== null) {
        preferences[action.action_key] = action.action_value;
      }
    }

    return preferences;
  } catch (error) {
    console.error('[userActionOperations] Error getting all preferences:', error);
    return {};
  }
}

/**
 * Delete a user preference
 */
export async function deleteUserPreference(
  key: string
): Promise<{ success: boolean; error?: string }> {
  return deleteUserAction('preference', key);
}

// ============================================================================
// Dismissal Operations
// ============================================================================

/**
 * Dismiss an item with optional expiry
 */
export async function dismissItem(
  key: string,
  days?: number
): Promise<{ success: boolean; error?: string }> {
  let expiresAt: string | null = null;

  if (days && days > 0) {
    const expiry = new Date();
    expiry.setDate(expiry.getDate() + days);
    expiresAt = expiry.toISOString();
  }

  return setUserAction('dismissal', key, null, expiresAt);
}

/**
 * Check if an item is dismissed
 */
export function isDismissed(key: string): boolean {
  const action = getUserAction('dismissal', key);
  return action !== null;
}

/**
 * Get all dismissed items (non-expired)
 */
export function getDismissedItems(): string[] {
  try {
    const actions = getUserActions('dismissal');
    return actions.map((a) => a.action_key);
  } catch (error) {
    console.error('[userActionOperations] Error getting dismissed items:', error);
    return [];
  }
}

/**
 * Get all dismissed items with their expiry times
 */
export function getDismissedItemsWithExpiry(): Record<string, string | null> {
  try {
    const actions = getUserActions('dismissal');
    const dismissed: Record<string, string | null> = {};

    for (const action of actions) {
      dismissed[action.action_key] = action.expires_at;
    }

    return dismissed;
  } catch (error) {
    console.error('[userActionOperations] Error getting dismissed items with expiry:', error);
    return {};
  }
}

/**
 * Undismiss an item
 */
export async function undismissItem(key: string): Promise<{ success: boolean; error?: string }> {
  return deleteUserAction('dismissal', key);
}

// ============================================================================
// UI State Operations
// ============================================================================

/**
 * Set UI state value
 */
export async function setUIState(
  key: string,
  value: string
): Promise<{ success: boolean; error?: string }> {
  return setUserAction('ui_state', key, value, null);
}

/**
 * Get UI state value
 */
export function getUIState(key: string): string | null {
  const action = getUserAction('ui_state', key);
  return action?.action_value || null;
}

/**
 * Get all UI state as a key-value object
 */
export function getAllUIState(): Record<string, string> {
  try {
    const actions = getUserActions('ui_state');
    const state: Record<string, string> = {};

    for (const action of actions) {
      if (action.action_value !== null) {
        state[action.action_key] = action.action_value;
      }
    }

    return state;
  } catch (error) {
    console.error('[userActionOperations] Error getting all UI state:', error);
    return {};
  }
}

/**
 * Delete UI state
 */
export async function deleteUIState(key: string): Promise<{ success: boolean; error?: string }> {
  return deleteUserAction('ui_state', key);
}

/**
 * Clear all UI state (useful for logout/reset)
 */
export async function clearAllUIState(): Promise<{ success: boolean; error?: string }> {
  try {
    execute('DELETE FROM user_actions WHERE action_type = ?', ['ui_state']);
    await saveDB();
    return { success: true };
  } catch (error) {
    console.error('[userActionOperations] Error clearing UI state:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to clear UI state'
    };
  }
}
